'use client';
import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import { useLocale, useTranslations as useTranslations$1 } from 'next-intl';
import { Loader2, Check, AlertCircle, ArrowRight, ArrowLeft } from '@/components/ui/icons';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { upgradeToGoogleAccount, signInToExistingAccount, signInToExistingGoogleAccount, signInWithSSO, upgradeWithVerifiedEmail, requestPasswordReset, isAnonymousAuthDisabled, isEnterpriseSsoEnabled, type UpgradeError } from '@/lib/auth';
import { reconnectProvider } from '@/lib/multiplayer/bootstrap';
import { useAuth } from '@/hooks/useAuth';
import { AuthCaptchaContainer } from '@/contexts/AuthContext';
import { getStoredReferralCode, getStoredPromoSlug, hasStoredSignupAccess, isSentinelRefCode, MOTION_SHARE_REF_CODE } from '@/lib/signupAccessStorage';
import { useStoredSignupAccess } from '@/hooks/useStoredSignupAccess';
import { getPasswordPolicy, getReferrer, resolveSso, sendOTP, verifyOTPCode, STANDARD_PASSWORD_POLICY } from '@/lib/api';
import type { PasswordPolicy } from '@/lib/api';
import { checkPasswordPolicy } from '@/lib/passwordPolicy';
import { PasswordRequirements } from '@/components/auth/PasswordRequirements';
import { getVisitorId } from '@/lib/fingerprint';
import { trackSignupError, trackSignupExistingAccountSwitched, trackSignupModalViewed, trackSignupStepCompleted, trackSignupStepViewed, type SignupSource, type SignupStep as AnalyticsSignupStep } from '@/lib/analytics';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { useAuthCaptcha } from './useAuthCaptcha';
import { AuthCaptchaError } from '@/lib/authCaptcha';
import { OTPInput } from '@/components/auth/OTPInput';
import { useOTPCountdown } from '@/hooks/useOTPCountdown';
type SignUpStep = 'email-entry' | 'otp-verification' | 'password-entry';
type AuthMode = 'signup' | 'signin' | 'forgot-password';
interface ReferrerInfo {
    alias: string;
    color: string;
}
interface SignUpModalProps {
    isOpen: boolean;
    onClose: () => void;
    /**
   * Auth completed. Receives the destination {@link resolvePostAuthPath} resolved for
   * what ACTUALLY happened (signup vs sign-in) — callers that navigate should
   * use it instead of hardcoding a path, or a new account lands on /projects
   * and flashes it before /projects re-derives the same /welcome decision.
   */ onSuccess?: (destination: string) => void;
    projectId?: string;
    returnPath?: string;
    defaultMode?: 'signup' | 'signin';
    /** Modal variant: project context, consumer marketing, or dedicated enterprise login. */ variant?: 'project' | 'marketing' | 'enterprise';
    /** Pre-fill the email field (e.g. from a team invitation) */ initialEmail?: string;
    /** Override the inviter name shown in the modal (e.g. from a promo campaign) */ inviterName?: string;
    /** Explicit source/referral code for attribution-only surfaces like public motion shares. */ referralCode?: string | null;
    /** Funnel source for signup-step analytics and source-specific copy. */ source?: SignupSource;
}
/**
 * Signup surfaces (/invite, /early-access, the marketing home) redirect an
 * already-signed-in visitor to /projects. Completing a signup makes THIS
 * session signed-in partway through the request — `upgradeWithVerifiedEmail`
 * re-authenticates with the new credentials before conversion finishes — so
 * those ambient redirects fire on the half-finished signup and hard-load
 * /projects before the modal can route a new account to /welcome. That is the
 * /projects flash.
 *
 * One auth flow per tab, so a module-scope latch is enough: set when the modal
 * starts an auth request, cleared only if that request fails. Once it succeeds
 * the modal navigates and the page unloads, so the latch never needs clearing.
 */ let modalOwnsNavigation = false;
/** True once SignUpModal has taken over where this session goes. */ export function signUpModalOwnsNavigation(): boolean {
    return modalOwnsNavigation;
}
// Setters, not direct assignment: the React Compiler lint rule (rightly) bars a
// component from reassigning a module-scope binding.
function claimNavigation(): void {
    modalOwnsNavigation = true;
}
function releaseNavigation(): void {
    modalOwnsNavigation = false;
}
// Email validation regex - requires valid domain with TLD (at least 2 chars)
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
function validateEmail(email: string): boolean {
    return EMAIL_REGEX.test(email.trim());
}
/**
 * Destination after auth completes. The generic destinations '/' and
 * '/projects' are rewritten (every marketing surface passes
 * returnPath="/projects" and means "the app", not that page): promo holders go
 * to plan selection, brand-new accounts go straight to onboarding instead of
 * flashing /projects while it re-derives the same decision. Any other explicit
 * path (a project, an invite) is respected verbatim and reported as not
 * rewritten so the caller can leave the host page alone.
 */ export function resolvePostAuthPath({ returnPath, projectId, mode, hasPromo }: {
    returnPath?: string;
    projectId?: string;
    mode: AuthMode;
    hasPromo: boolean;
}): {
    destination: string;
    rewritten: boolean;
} {
    const explicit = returnPath || (projectId ? `/p/${projectId}` : '/');
    if (explicit === '/' || explicit === '/projects') {
        if (hasPromo) return {
            destination: '/select-plan',
            rewritten: true
        };
        if (mode === 'signup') return {
            destination: '/welcome',
            rewritten: true
        };
    }
    return {
        destination: explicit,
        rewritten: false
    };
}
function useErrorMessageBuilder(): (error: UpgradeError) => string {
    const t = useTranslations$1();
    return (error: UpgradeError)=>{
        switch(error){
            case 'email_exists':
                return t("muKzGo");
            case 'weak_password':
                return t("VX9Kkg");
            case 'invalid_email':
                return t("oLpv29");
            default:
                return t("W_5hwr");
        }
    };
}
export function SignUpModal({ isOpen, onClose, onSuccess, projectId, returnPath, defaultMode = 'signup', variant = 'project', initialEmail, inviterName: inviterNameProp, referralCode: referralCodeProp, source = 'default' }: SignUpModalProps) {
    const t = useTranslations$1();
    const getErrorMessage = useErrorMessageBuilder();
    const { isAnonymous, isLoading: isAuthLoading } = useAuth();
    const locale = useLocale();
    const router = useRouter();
    // On SSO-only deployments there's no anonymous session to upgrade, so the
    // self-serve signup flow (OTP/password upgrade APIs) can't run and would fail
    // with MISSING_TOKEN. Coerce a caller-requested signup default (marketing/
    // promo CTAs) to sign-in; the "switch to sign up" toggle is also hidden below.
    const effectiveDefaultMode = isAnonymousAuthDisabled() && defaultMode === 'signup' ? 'signin' : defaultMode;
    const enterpriseSsoEnabled = variant === 'enterprise' || isEnterpriseSsoEnabled();
    const [mode, setMode] = useState<AuthMode>(effectiveDefaultMode);
    const { getCaptchaToken, captcha } = useAuthCaptcha(mode === 'forgot-password' ? 'password_reset' : mode === 'signup' ? 'signup_signin' : 'password_signin', isOpen);
    const [signUpStep, setSignUpStep] = useState<SignUpStep>('email-entry');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [passwordPolicy, setPasswordPolicy] = useState<PasswordPolicy>(STANDARD_PASSWORD_POLICY);
    const [otp, setOtp] = useState('');
    const [verificationToken, setVerificationToken] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isGoogleLoading, setIsGoogleLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Enterprise SSO (Home Realm Discovery): set when the entered email's domain is
    // SSO-enabled, which surfaces the "Continue with SSO" path.
    const [ssoState, setSsoState] = useState<{
        providerId: string | null;
        orgName: string | null;
    } | null>(null);
    const [isSsoLoading, setIsSsoLoading] = useState(false);
    // Mirrors the latest email so async HRD responses can detect they're stale
    // (the field was edited while the request was in flight).
    const emailRef = useRef(email);
    useEffect(()=>{
        emailRef.current = email;
    }, [
        email
    ]);
    const [resetEmailSent, setResetEmailSent] = useState(false);
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);
    const turnstileRef = useRef<TurnstileInstance | null>(null);
    const otpCountdown = useOTPCountdown({
        cooldownSeconds: 60
    });
    // Onboarding funnel instrumentation. Step viewed/completed events fire once
    // per modal open; the refs are reset by the open effect below.
    const viewedStepsRef = useRef<Set<AnalyticsSignupStep>>(new Set());
    const completedStepsRef = useRef<Set<AnalyticsSignupStep>>(new Set());
    const turnstileFailureTrackedRef = useRef(false);
    const wasOpenRef = useRef(false);
    const trackStepCompletedOnce = (step: AnalyticsSignupStep)=>{
        if (completedStepsRef.current.has(step)) return;
        completedStepsRef.current.add(step);
        trackSignupStepCompleted({
            step,
            source
        });
    };
    // Turnstile auto-retries failures, so report the widget failing at most
    // once per modal open instead of once per retry. Load failure (widget
    // broken) and interaction timeout (user walked away from the challenge)
    // are distinct funnel signals.
    const handleTurnstileFailure = (code: 'turnstile_load_failed' | 'turnstile_timeout')=>{
        if (turnstileFailureTrackedRef.current) return;
        turnstileFailureTrackedRef.current = true;
        trackSignupError({
            step: 'email_entry',
            code,
            source
        });
    };
    // Shared content sizing for every branch of this modal: founder polish adds
    // a touch more horizontal breathing room than the p-6 DialogContent default.
    const authContentClass = 'max-w-md px-8';
    // Real-time validation
    const passwordCheck = checkPasswordPolicy(password, passwordPolicy);
    const isEmailValid = email.trim() === '' || validateEmail(email);
    const isPasswordValid = mode === 'signin' || password.length === 0 || passwordCheck.ok;
    const canSubmitSignIn = isEmailValid && isPasswordValid && email.trim() !== '' && password.trim() !== '';
    const canSubmitEmail = !isAuthLoading && isEmailValid && email.trim() !== '' && !!captchaToken;
    const canSubmitForgotPassword = isEmailValid && email.trim() !== '';
    const canSubmitOTP = otp.length === 6;
    const canSubmitPassword = passwordCheck.ok;
    // Referral state
    const [referralCode, setReferralCode] = useState<string | null>(()=>referralCodeProp ?? null);
    const [referrer, setReferrer] = useState<ReferrerInfo | null>(null);
    const [referralError, setReferralError] = useState<string | null>(null);
    // Reactive, not a mount-time snapshot: on the promo landing page the slug is
    // stored only after the async campaign fetch, while this modal is already
    // mounted (closed) by the nav — a snapshot would gate promo holders into the
    // waitlist screen.
    const hasStoredAccess = useStoredSignupAccess();
    const hasSignupAccess = !!referralCode || hasStoredAccess;
    const isMotionShareSignup = source === 'motion_share' || referralCode === MOTION_SHARE_REF_CODE;
    // Reset state when modal opens
    useEffect(()=>{
        if (isOpen) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- modal state must reset on explicit open.
            setMode(effectiveDefaultMode);
            setSignUpStep('email-entry');
            setOtp('');
            setVerificationToken(null);
            setPasswordPolicy(STANDARD_PASSWORD_POLICY);
            setResetEmailSent(false);
            setCaptchaToken(null);
            setSsoState(null);
            setIsSsoLoading(false);
            turnstileRef.current?.reset();
            otpCountdown.reset();
            viewedStepsRef.current.clear();
            completedStepsRef.current.clear();
            turnstileFailureTrackedRef.current = false;
            if (initialEmail) {
                setEmail(initialEmail);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only reset when modal opens, not when countdown changes
    }, [
        isOpen,
        defaultMode,
        initialEmail
    ]);
    // Track modal viewed for onboarding funnel
    useEffect(()=>{
        if (isOpen && isAnonymous) {
            // Enterprise is a presentation-only specialization of the existing
            // in-app sign-in gate, not a new analytics funnel variant.
            trackSignupModalViewed({
                variant: variant === 'enterprise' ? 'project' : variant,
                mode: defaultMode,
                source
            });
        }
    }, [
        isOpen,
        isAnonymous,
        variant,
        defaultMode,
        source
    ]);
    // Track signup step views for the onboarding funnel, once per step per modal
    // open. Derived from render state so every path into a step — opening the
    // modal, forward transitions, the sign-in ↔ sign-up switches, and the async
    // waitlist-gate unlock — is covered in one place.
    useEffect(()=>{
        if (!isOpen) {
            wasOpenRef.current = false;
            return;
        }
        // On the render where the modal just opened, mode/signUpStep may still
        // hold values from a previous session (the reset effect's setState lands
        // on the next render), so substitute the post-reset values.
        const justOpened = !wasOpenRef.current;
        wasOpenRef.current = true;
        const effectiveMode = justOpened ? defaultMode : mode;
        const effectiveStep = justOpened ? 'email-entry' : signUpStep;
        if (!isAnonymous || effectiveMode !== 'signup') return;
        const step: AnalyticsSignupStep = variant === 'marketing' && !hasSignupAccess ? 'waitlist_gate' : effectiveStep === 'otp-verification' ? 'otp_verification' : effectiveStep === 'password-entry' ? 'password_entry' : 'email_entry';
        if (viewedStepsRef.current.has(step)) return;
        viewedStepsRef.current.add(step);
        trackSignupStepViewed({
            step,
            source
        });
    }, [
        isOpen,
        isAnonymous,
        mode,
        defaultMode,
        variant,
        hasSignupAccess,
        signUpStep,
        source
    ]);
    // Eagerly start fingerprint collection when modal opens
    useEffect(()=>{
        if (isOpen) {
            getVisitorId(); // fire-and-forget, result is cached
        }
    }, [
        isOpen
    ]);
    // Fetch referrer info when modal opens.
    // If the parent page provides an inviterName prop (e.g. promo landing page),
    // use that directly. Otherwise fall back to the stored referral code's referrer.
    useEffect(()=>{
        if (!isOpen) return;
        const code = referralCodeProp ?? getStoredReferralCode();
        // eslint-disable-next-line react-hooks/set-state-in-effect -- reflect stored referral state when opening signup.
        setReferralCode(code);
        if (inviterNameProp) {
            setReferrer({
                alias: inviterNameProp,
                color: '#6366f1'
            });
            setReferralError(null);
            return;
        }
        if (code && !isSentinelRefCode(code)) {
            getReferrer(code).then((data)=>{
                setReferrer(data);
                setReferralError(data ? null : t("pgjNNJ"));
            }).catch(()=>{
                setReferrer(null);
                setReferralError(t("pgjNNJ"));
            });
        } else {
            // Sentinel codes (motion_share, AI-assistant referrals) are source signals,
            // not real referrers — never look one up (it would 404) or surface an
            // "invalid code" error.
            setReferrer(null);
            setReferralError(null);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- useExtracted returns a new function each render; keep referrer lookup tied to modal/inviter changes.
    }, [
        isOpen,
        inviterNameProp,
        referralCodeProp
    ]);
    // Step 1: Send OTP to email
    const handleSendOTP = async (e: React.FormEvent)=>{
        e.preventDefault();
        if (isAuthLoading) return;
        setError(null);
        if (!validateEmail(email)) {
            setError(t("oLpv29"));
            return;
        }
        // If this domain is SSO-enabled, redirect to the IdP instead of emailing an OTP
        // (closes the HRD-on-blur race where a fast submit would bypass SSO).
        if (await maybeRouteToSso()) return;
        setIsLoading(true);
        try {
            const result = await sendOTP(email, captchaToken, locale);
            if (result.success) {
                trackStepCompletedOnce('email_entry');
                setSignUpStep('otp-verification');
                otpCountdown.start();
            } else {
                if (result.error === 'EMAIL_ALREADY_REGISTERED') {
                    trackSignupExistingAccountSwitched({
                        source
                    });
                    // Auto-switch to sign-in with email preserved. The backend's copy
                    // says whether this exact spelling is registered or only the inbox
                    // is (a Gmail dot / +tag permutation) — a password login needs the
                    // spelling that was registered, so that difference has to show.
                    setMode('signin');
                    setPassword('');
                    setError(result.message);
                } else if (result.error === 'EMAIL_DOMAIN_BLOCKED') {
                    setError(result.message);
                } else if (result.error === 'RATE_LIMITED') {
                    setError(result.message);
                } else if (result.error === 'TURNSTILE_FAILED') {
                    trackSignupError({
                        step: 'email_entry',
                        code: 'turnstile_rejected',
                        source
                    });
                    setCaptchaToken(null);
                    turnstileRef.current?.reset();
                    setError(t("AsX3qN"));
                } else {
                    trackSignupError({
                        step: 'email_entry',
                        code: 'send_otp_failed',
                        source
                    });
                    setError(result.message || t("VNzyum"));
                }
            }
        } catch (err) {
            console.error('[SignUpModal] sendOTP error:', err);
            trackSignupError({
                step: 'email_entry',
                code: 'send_otp_failed',
                source
            });
            setError(t("W_5hwr"));
        } finally{
            setIsLoading(false);
        }
    };
    // Step 2: Verify OTP
    const submitOTP = async (otpValue: string)=>{
        setError(null);
        if (otpValue.length !== 6) {
            setError(t("dD1ZD5"));
            return;
        }
        setIsLoading(true);
        try {
            const result = await verifyOTPCode(email, otpValue);
            if (result.success) {
                trackStepCompletedOnce('otp_verification');
                let policy = STANDARD_PASSWORD_POLICY;
                try {
                    policy = await getPasswordPolicy(email, result.verificationToken);
                } catch (err) {
                    console.warn('[SignUpModal] password policy lookup failed after OTP verification:', err);
                }
                setPasswordPolicy(policy);
                setVerificationToken(result.verificationToken);
                setSignUpStep('password-entry');
            } else {
                setError(result.message || t("poE3OD"));
                if (result.error === 'TOO_MANY_ATTEMPTS' || result.error === 'OTP_EXPIRED' || result.error === 'NOT_FOUND') {
                    trackSignupError({
                        step: 'otp_verification',
                        // NOT_FOUND means no pending code exists for this email, which from
                        // the user's perspective is an expired code.
                        code: result.error === 'TOO_MANY_ATTEMPTS' ? 'otp_too_many_attempts' : 'otp_expired',
                        source
                    });
                    // Reset to email step so user can request a new code
                    setOtp('');
                } else {
                    trackSignupError({
                        step: 'otp_verification',
                        code: 'otp_invalid',
                        source
                    });
                }
            }
        } catch  {
            setError(t("W_5hwr"));
        } finally{
            setIsLoading(false);
        }
    };
    const handleVerifyOTP = async (e: React.FormEvent)=>{
        e.preventDefault();
        await submitOTP(otp);
    };
    // Step 3: Complete signup with password
    const handleCompleteSignup = async (e: React.FormEvent)=>{
        e.preventDefault();
        setError(null);
        if (!passwordCheck.ok) {
            setError(t("53sood"));
            return;
        }
        if (!verificationToken) {
            setError(t("K2UNX7"));
            setSignUpStep('email-entry');
            return;
        }
        setIsLoading(true);
        claimNavigation();
        try {
            const result = await upgradeWithVerifiedEmail(email, password, verificationToken, getCaptchaToken, locale);
            if (result.success) {
                trackStepCompletedOnce('password_entry');
                // signup_completed event is now emitted server-side in completeConversion
                if (projectId) {
                    reconnectProvider(projectId);
                }
                if (onSuccess) {
                    onSuccess(getPostAuthPath().destination);
                } else {
                    // The marketing page's ambient authed→/projects redirect is standing
                    // down for us (see `signUpModalOwnsNavigation`), so a new account
                    // lands on /welcome without the /projects flash.
                    navigateAfterAuth();
                }
                onClose();
            } else {
                releaseNavigation();
                if (result.error === 'email_exists') {
                    trackSignupExistingAccountSwitched({
                        source
                    });
                    // Auto-switch to sign-in with email preserved
                    setMode('signin');
                    setSignUpStep('email-entry');
                    setPassword('');
                } else {
                    trackSignupError({
                        step: 'password_entry',
                        code: 'password_set_failed',
                        source
                    });
                    setError(result.message || getErrorMessage(result.error!));
                }
            }
        } catch  {
            releaseNavigation();
            trackSignupError({
                step: 'password_entry',
                code: 'password_set_failed',
                source
            });
            setError(t("W_5hwr"));
        } finally{
            setIsLoading(false);
        }
    };
    // Resend OTP
    const handleResendOTP = async ()=>{
        if (otpCountdown.isActive) return;
        setError(null);
        setIsLoading(true);
        try {
            const result = await sendOTP(email, null, locale);
            if (result.success) {
                otpCountdown.start();
                setOtp('');
            } else {
                trackSignupError({
                    step: 'otp_verification',
                    code: 'send_otp_failed',
                    source
                });
                setError(result.message || t("j6yQ9W"));
            }
        } catch  {
            trackSignupError({
                step: 'otp_verification',
                code: 'send_otp_failed',
                source
            });
            setError(t("W_5hwr"));
        } finally{
            setIsLoading(false);
        }
    };
    // Go back to email entry
    const handleBackToEmail = ()=>{
        setSignUpStep('email-entry');
        setOtp('');
        setVerificationToken(null);
        setError(null);
        setCaptchaToken(null);
        turnstileRef.current?.reset();
        otpCountdown.reset();
    };
    // Sign in handler (unchanged flow)
    const handleSignIn = async (e: React.FormEvent)=>{
        e.preventDefault();
        setError(null);
        if (!validateEmail(email)) {
            setError(t("oLpv29"));
            return;
        }
        // If this domain is SSO-enabled, redirect to the IdP instead of password sign-in.
        if (await maybeRouteToSso()) return;
        setIsLoading(true);
        claimNavigation();
        try {
            const result = await signInToExistingAccount(email, password, await getCaptchaToken());
            if (result.success) {
                if (projectId) {
                    reconnectProvider(projectId);
                }
                onSuccess?.(getPostAuthPath().destination);
                onClose();
            } else {
                releaseNavigation();
                setError(result.error || t("yBBdAn"));
            }
        } catch (err) {
            releaseNavigation();
            if (!(err instanceof AuthCaptchaError && err.reason === 'cancelled')) {
                setError(t("W_5hwr"));
            }
        } finally{
            setIsLoading(false);
        }
    };
    // Shared by the password, Google, and SSO paths (see resolvePostAuthPath).
    const getPostAuthPath = ()=>resolvePostAuthPath({
            returnPath,
            projectId,
            mode,
            hasPromo: !!getStoredPromoSlug()
        });
    // The marketing variant always navigates (its host page has no post-auth
    // handling of its own). Every other variant navigates only when the generic
    // destination was rewritten to onboarding / plan selection: an explicit path
    // is the host page's business — it may already be open under a sub-canvas
    // slug or with a query string that a replace() would clobber. This is what
    // used to send SignedOutGate signups through the /projects loader before
    // /welcome.
    const navigateAfterAuth = ()=>{
        const { destination, rewritten } = getPostAuthPath();
        if (variant === 'marketing' || rewritten) router.replace(destination);
    };
    const handleGoogleAuth = async ()=>{
        setError(null);
        setIsGoogleLoading(true);
        try {
            const path = getPostAuthPath().destination;
            // Anonymous visitors holding an access code take the signup
            // (identity-link) path even from the "Log in" view: production blocks
            // brand-new users on the plain sign-in path ("Database error saving new
            // user" → dead-end NO_ACCOUNT screen), while the auth callback already
            // recovers existing accounts from linkIdentity's "identity already
            // linked" error by retrying as a sign-in. On deployments without
            // anonymous auth there is no session to link onto — linkIdentity would
            // throw — so those always take the plain sign-in path.
            if (!isAnonymousAuthDisabled() && (mode === 'signup' || (isAnonymous && (hasSignupAccess || hasStoredSignupAccess())))) {
                await upgradeToGoogleAccount(path);
            } else {
                await signInToExistingGoogleAccount(path);
            }
        } catch  {
            // Step events cover the email flow; Google success completes server-side
            // (signup_completed) after the redirect, so only the start failure is
            // observable from here.
            trackSignupError({
                step: 'email_entry',
                code: 'google_start_failed',
                source
            });
            setError(mode === 'signup' ? t("oH2jFi") : t("8aTtVe"));
            setIsGoogleLoading(false);
        }
    };
    // Home Realm Discovery: when the entered work email's domain is SSO-enabled,
    // surface the SSO path. Runs on email blur; failures are non-blocking (the user
    // simply continues with the normal password/Google flow).
    const checkSsoForEmail = async ()=>{
        if (!enterpriseSsoEnabled) {
            setSsoState(null);
            return;
        }
        if (!validateEmail(email)) {
            setSsoState(null);
            return;
        }
        const requestedEmail = email;
        try {
            const result = await resolveSso(requestedEmail);
            // Stale-response guard: the user may have edited the email while the HRD
            // request was in flight — never surface SSO state for the old address.
            if (emailRef.current !== requestedEmail) return;
            setSsoState(result.ssoEnabled ? {
                providerId: result.providerId ?? null,
                orgName: result.orgName ?? null
            } : null);
        } catch  {
            if (emailRef.current === requestedEmail) setSsoState(null);
        }
    };
    // Start the SAML redirect. Resolves the domain first if we haven't already (the
    // explicit "Use single sign-on" link path, or a fast form submit), so it doubles
    // as the manual entry point. signInWithSSO redirects the browser on success.
    const handleSsoLogin = async (preResolved?: {
        providerId: string | null;
        orgName: string | null;
    })=>{
        if (!enterpriseSsoEnabled) return;
        setError(null);
        if (!validateEmail(email)) {
            setError(t("Wnjy37"));
            return;
        }
        setIsSsoLoading(true);
        try {
            let sso = preResolved ?? ssoState;
            if (!sso) {
                const result = await resolveSso(email);
                if (!result.ssoEnabled) {
                    setError(t("ETtwRJ"));
                    setIsSsoLoading(false);
                    return;
                }
                sso = {
                    providerId: result.providerId ?? null,
                    orgName: result.orgName ?? null
                };
                setSsoState(sso);
            }
            await signInWithSSO(sso.providerId ? {
                providerId: sso.providerId
            } : {
                domain: email.split('@')[1]?.toLowerCase() ?? ''
            }, getPostAuthPath().destination);
        // signInWithSSO redirects to the IdP; nothing else runs on success.
        } catch  {
            setError(t("ji4pqv"));
            setIsSsoLoading(false);
        }
    };
    // Submit-time SSO guard: HRD runs async on email blur, so a fast Enter/Continue
    // could otherwise start the password/OTP flow for an SSO-enabled domain. Returns
    // true once it has initiated the SAML redirect, so the caller aborts its flow.
    const maybeRouteToSso = async (): Promise<boolean> =>{
        if (!enterpriseSsoEnabled) return false;
        let sso = ssoState;
        if (!sso) {
            try {
                const result = await resolveSso(email);
                if (result.ssoEnabled) sso = {
                    providerId: result.providerId ?? null,
                    orgName: result.orgName ?? null
                };
            } catch  {
                return false;
            }
        }
        if (!sso) return false;
        await handleSsoLogin(sso);
        return true;
    };
    // Forgot password handler
    const handleForgotPassword = async (e: React.FormEvent)=>{
        e.preventDefault();
        setError(null);
        if (!validateEmail(email)) {
            setError(t("oLpv29"));
            return;
        }
        setIsLoading(true);
        try {
            const result = await requestPasswordReset(email, await getCaptchaToken());
            if (result.success) {
                setResetEmailSent(true);
            } else {
                setError(result.error || t("pbp6JQ"));
            }
        } catch (err) {
            if (!(err instanceof AuthCaptchaError && err.reason === 'cancelled')) {
                setError(t("W_5hwr"));
            }
        } finally{
            setIsLoading(false);
        }
    };
    // Logged-in user view - show they already have access.
    //
    // NOT while one of this modal's own auth requests is in flight: completing a
    // signup re-authenticates (upgradeWithVerifiedEmail signs in with the new
    // credentials) BEFORE conversion finishes, so the session stops being
    // anonymous mid-submit. Without the `isLoading` guard that flips the
    // "Creating account…" form to this screen for the last second of a signup.
    if (!isAnonymous && !isLoading) {
        return (<Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className={authContentClass}>
          <AuthCaptchaContainer/>
          <DialogHeader className="text-center pb-2">
            <div className="mx-auto w-10 h-10 rounded-full bg-muted flex items-center justify-center mb-4">
              <Check className="w-5 h-5 text-foreground"/>
            </div>
            <DialogTitle className="text-xl font-semibold">{t("rar0rz")}</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {t("FwnbM1")}
            </DialogDescription>
          </DialogHeader>

          <Button onClick={onClose} className="w-full mt-4">
            {t("KdESMh")}
          </Button>
        </DialogContent>
      </Dialog>);
    }
    // Marketing variant: Invite-required gate for sign-up without referral or promo code
    if (variant === 'marketing' && mode === 'signup' && !hasSignupAccess) {
        return (<Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className={authContentClass} overlayClassName="bg-black/60 backdrop-blur-sm">
          <AuthCaptchaContainer/>
          <DialogHeader className="text-center pb-4">
            <div className="flex flex-col items-center gap-2 mb-0">
              <div className="w-12 h-12 flex items-center justify-center">
                { /* eslint-disable-next-line i18next/no-literal-string -- brand */ }
                <Image src="/whitemartini.svg" alt="Martini" width={32} height={30}/>
              </div>
              <DialogTitle className="text-xl font-semibold text-center">{t("h6_d7P")}</DialogTitle>
            </div>
            <DialogDescription className="text-center mt-2">
              {t("Pk8n-G")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            <Button onClick={()=>{
            onClose();
            window.open('https://form.typeform.com/to/VVwyeUrj', '_blank', 'noopener,noreferrer');
        }} className="w-full h-11 font-medium bg-black text-white hover:bg-black/90 active:bg-black/95 shadow-lg hover:shadow-xl transition-all group">
              {t("UyaPHa")}
              <ArrowRight className="w-4 h-4 ms-2 transition-transform group-hover:translate-x-1"/>
            </Button>

            <div className="text-center pt-2">
              <button type="button" onClick={()=>{
            setMode('signin');
            setError(null);
            setPassword('');
        }} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                {t("uCk8r-")} <span className="font-medium text-foreground">{t("odXlk8")}</span>
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>);
    }
    // Anonymous user view - prompt to sign up or sign in
    return (<Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={authContentClass} overlayClassName={variant === 'marketing' ? 'bg-black/60 backdrop-blur-sm' : variant === 'enterprise' ? 'bg-background/80 backdrop-blur-sm' : undefined} showCloseButton={variant !== 'enterprise'}>
        <DialogHeader className="text-center pb-4">
          <div className={cn('flex flex-col items-center', variant === 'marketing' ? 'gap-2 mb-0' : 'gap-2.5 mb-1')}>
            <div className="w-12 h-12 flex items-center justify-center">
              { /* eslint-disable-next-line i18next/no-literal-string -- brand */ }
              <Image src="/whitemartini.svg" alt="Martini" width={32} height={30}/>
            </div>
            <DialogTitle className="text-xl font-semibold text-center">
              {mode === 'forgot-password' ? t("Yy_yDL") : mode === 'signin' ? variant === 'enterprise' ? 'Sign in to Martini Enterprise' : t("UKxoV8") : signUpStep === 'otp-verification' ? t("zx7CKB") : signUpStep === 'password-entry' ? t("NXxPgz") : isMotionShareSignup ? t("kH7CNz") : t("SeO7GZ")}
            </DialogTitle>
          </div>
          {mode === 'signup' && signUpStep === 'email-entry' && (<DialogDescription className="text-center text-xs leading-relaxed text-muted-foreground/70 mt-1.5">
              {t.rich("vtb6b9", {
        break: ()=><br/>,
        terms: (chunks)=>(<a href="/terms" target="_blank" rel="noopener noreferrer" className="font-medium text-muted-foreground underline hover:text-foreground transition-colors">
                      {chunks}
                    </a>),
        privacy: (chunks)=>(<a href="/privacy" target="_blank" rel="noopener noreferrer" className="font-medium text-muted-foreground underline hover:text-foreground transition-colors">
                      {chunks}
                    </a>)
    })}
            </DialogDescription>)}
          {variant === 'enterprise' && mode === 'signin' && (<DialogDescription className="mt-1.5 text-center text-sm text-muted-foreground">
              Use your work email to continue to your organization.
            </DialogDescription>)}
        </DialogHeader>

        {mode === 'signup' && signUpStep === 'email-entry' && isMotionShareSignup && (<div className="-mt-1 mb-4 rounded-lg border border-border bg-secondary/35 p-3 text-start">
            <p className="text-sm font-medium text-foreground">{t("H0asU2")}</p>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              {t("_yTtcK")}
            </p>
          </div>)}

        <AuthCaptchaContainer/>
        {captcha}

        { /* Referral indicator - show prominently at top during email entry */ }
        {mode === 'signup' && signUpStep === 'email-entry' && referrer && (<div className={cn('flex items-center gap-3 p-4 rounded-lg mb-4', 'bg-muted/50 border border-border')}>
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0" style={{
        backgroundColor: referrer.color
    }}>
              {referrer.alias.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">
                {t("VMOR_6", {
        name: referrer.alias
    })}
              </p>
            </div>
          </div>)}

        { /* Referral error warning - only during email entry */ }
        {mode === 'signup' && signUpStep === 'email-entry' && referralError && (<div className="flex items-center gap-2 p-3 rounded-lg mb-4 bg-amber-500/10 border border-amber-500/20">
            <AlertCircle className="w-4 h-4 flex-shrink-0 text-amber-500"/>
            <p className="text-sm text-amber-600 dark:text-amber-400">{referralError}</p>
          </div>)}

        <div className={cn('space-y-5', variant === 'marketing' && 'space-y-4 -mt-1')}>
          { /* Error */ }
          {error && (<div className="p-3 rounded-lg border bg-destructive/10 border-destructive/20">
              <p className="text-sm text-destructive">{error}</p>
            </div>)}

          { /* Google button - only show during initial email entry or signin */ }
          {variant !== 'enterprise' && (mode === 'signin' || (mode === 'signup' && signUpStep === 'email-entry')) && (<>
              <Button type="button" variant="outline" className="w-full h-11 font-medium transition-all" onClick={handleGoogleAuth} disabled={isGoogleLoading || isLoading}>
                {isGoogleLoading ? (<Loader2 className="w-4 h-4 me-2 animate-spin"/>) : (<svg className="w-4 h-4 me-2" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>)}
                {t("FJ6r9E")}
              </Button>

              {enterpriseSsoEnabled && ssoState ? (<div className="space-y-2">
                  <Button type="button" className="w-full h-11 font-medium transition-all" onClick={()=>void handleSsoLogin()} disabled={isSsoLoading || isGoogleLoading || isLoading}>
                    {isSsoLoading ? (<>
                        <Loader2 className="w-4 h-4 me-2 animate-spin"/>
                        {t("ET_GW3")}
                      </>) : (t("qank7-"))}
                  </Button>
                  <p className="text-xs text-center text-muted-foreground">
                    {ssoState.orgName ? t("XtQdDn", {
        org: ssoState.orgName
    }) : t("aPjVrm")}
                  </p>
                </div>) : enterpriseSsoEnabled ? (<button type="button" onClick={()=>void handleSsoLogin()} disabled={isSsoLoading || isGoogleLoading || isLoading} className="w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50">
                  {t("HL6ApU")}
                </button>) : null}

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border"/>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-2 bg-card text-muted-foreground">{t("5FWfPO")}</span>
                </div>
              </div>
            </>)}

          { /* Sign In Form */ }
          {mode === 'signin' && variant === 'enterprise' && (<form onSubmit={(event)=>{
        event.preventDefault();
        void handleSsoLogin();
    }} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="enterprise-sso-email" className="text-sm font-medium">
                  Work email
                </Label>
                <Input id="enterprise-sso-email" name="enterprise-sso-email" type="email" aria-label="Work email" placeholder="you@company.com" value={email} onChange={(event)=>{
        setEmail(event.target.value);
        setError(null);
        setSsoState(null);
    }} onBlur={()=>void checkSsoForEmail()} required disabled={isSsoLoading} className={cn('h-11 transition-colors', email.trim() !== '' && !isEmailValid && 'border-white/30 focus-visible:ring-white/20')} autoComplete="username" autoFocus/>
              </div>

              {ssoState?.orgName && (<p className="text-center text-xs text-muted-foreground">{ssoState.orgName} uses single sign-on.</p>)}

              <Button type="submit" className="h-11 w-full font-medium transition-all" disabled={isSsoLoading || !isEmailValid || email.trim() === ''}>
                {isSsoLoading ? (<>
                    <Loader2 className="me-2 h-4 w-4 animate-spin"/>
                    Redirecting…
                  </>) : ('Continue with SSO')}
              </Button>
            </form>)}

          {mode === 'signin' && variant !== 'enterprise' && (<form onSubmit={handleSignIn} className={cn('space-y-4', variant === 'marketing' && 'space-y-5')}>
              <div className="space-y-2">
                <Input id="signin-email" name="signin-email" type="email" aria-label={t("sy-pv5")} placeholder={t("PwhG9W")} value={email} onChange={(e)=>{
        setEmail(e.target.value);
        setError(null);
        setSsoState(null);
    }} onBlur={()=>void checkSsoForEmail()} required disabled={isLoading || isGoogleLoading || isSsoLoading} className={cn('h-11 transition-colors', email.trim() !== '' && !isEmailValid && 'border-white/30 focus-visible:ring-white/20')} autoComplete="username"/>
              </div>

              <div className="space-y-2">
                <Label htmlFor="signin-password" className="text-sm font-medium">
                  {t("5sg7KC")}
                </Label>
                <Input id="signin-password" name="signin-password" type="password" placeholder={t("2GFjIN")} value={password} onChange={(e)=>{
        setPassword(e.target.value);
        setError(null);
    }} required disabled={isLoading || isGoogleLoading} className="h-11 transition-colors" autoComplete="current-password"/>
                <div className="flex justify-end">
                  <button type="button" onClick={()=>{
        setMode('forgot-password');
        setError(null);
        setResetEmailSent(false);
    }} className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                    {t("V_JHlm")}
                  </button>
                </div>
              </div>

              <Button type="submit" className={cn('w-full h-11 font-medium transition-all', variant === 'marketing' && 'bg-black text-white hover:bg-black/90 active:bg-black/95 shadow-lg hover:shadow-xl')} disabled={isLoading || isGoogleLoading || !canSubmitSignIn}>
                {isLoading ? (<>
                    <Loader2 className="w-4 h-4 me-2 animate-spin"/>
                    {t("AWVk03")}
                  </>) : (t("odXlk8"))}
              </Button>

              { /* Self-serve signup needs an anonymous session to upgrade (OTP
                  verify + linkIdentity). On deployments without anonymous auth
                  accounts come from SSO JIT provisioning, so hide the toggle
                  instead of offering a flow that dead-ends. */ }
              {!isAnonymousAuthDisabled() && (<div className={cn('text-center pt-2', variant === 'marketing' && 'pt-3')}>
                  <button type="button" onClick={()=>{
        setMode('signup');
        setSignUpStep('email-entry');
        setError(null);
        setPassword('');
    }} className="text-sm transition-colors text-muted-foreground hover:text-foreground">
                    {t("c-R-Eh")} <span className="font-medium text-foreground">{t("8HJxXG")}</span>
                  </button>
                </div>)}
            </form>)}

          { /* Forgot Password */ }
          {mode === 'forgot-password' && (<div className="space-y-4">
              {resetEmailSent ? (<div className="space-y-4">
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">
                      {t.rich("_Ah64o", {
        email,
        emailValue: (chunks)=><span className="font-medium text-foreground">{chunks}</span>
    })}
                    </p>
                  </div>
                  <div className="text-center pt-2">
                    <button type="button" onClick={()=>{
        setMode('signin');
        setError(null);
        setResetEmailSent(false);
    }} className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 mx-auto">
                      <ArrowLeft className="w-3 h-3"/>
                      {t("7tSUe8")}
                    </button>
                  </div>
                </div>) : (<form onSubmit={handleForgotPassword} className="space-y-4">
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">
                      {t("DZKa1Z")}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Input id="reset-email" name="reset-email" type="email" placeholder={t("PwhG9W")} value={email} onChange={(e)=>{
        setEmail(e.target.value);
        setError(null);
    }} required disabled={isLoading} className={cn('h-11 transition-colors', email.trim() !== '' && !isEmailValid && 'border-white/30 focus-visible:ring-white/20')} autoComplete="username" autoFocus/>
                  </div>

                  <Button type="submit" className="w-full h-11 font-medium transition-all" disabled={isLoading || !canSubmitForgotPassword}>
                    {isLoading ? (<>
                        <Loader2 className="w-4 h-4 me-2 animate-spin"/>
                        {t("82Y7Sa")}
                      </>) : (t("DkQ5Q8"))}
                  </Button>

                  <div className="text-center pt-2">
                    <button type="button" onClick={()=>{
        setMode('signin');
        setError(null);
    }} className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 mx-auto">
                      <ArrowLeft className="w-3 h-3"/>
                      {t("7tSUe8")}
                    </button>
                  </div>
                </form>)}
            </div>)}

          { /* Sign Up Step 1: Email Entry */ }
          {mode === 'signup' && signUpStep === 'email-entry' && (<form onSubmit={handleSendOTP} className={cn('space-y-4', variant === 'marketing' && 'space-y-5')}>
              <div className="space-y-2">
                <Input id="signup-email" name="signup-email" type="email" aria-label={t("sy-pv5")} placeholder={t("PwhG9W")} value={email} onChange={(e)=>{
        setEmail(e.target.value);
        setError(null);
        setSsoState(null);
    }} onBlur={()=>void checkSsoForEmail()} required disabled={isLoading || isGoogleLoading || isSsoLoading} className={cn('h-11 transition-colors', email.trim() !== '' && !isEmailValid && 'border-white/30 focus-visible:ring-white/20')} autoComplete="username"/>
                {email.trim() !== '' && !isEmailValid && (<p className="text-xs text-white/50">{t("oLpv29")}</p>)}
              </div>

              <Turnstile ref={turnstileRef} siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY!} onSuccess={setCaptchaToken} onExpire={()=>setCaptchaToken(null)} onError={()=>handleTurnstileFailure('turnstile_load_failed')} onTimeout={()=>handleTurnstileFailure('turnstile_timeout')} options={{
        size: 'flexible',
        theme: 'dark',
        appearance: 'interaction-only'
    }}/>

              <Button type="submit" className={cn('w-full h-11 font-medium transition-all', variant === 'marketing' && 'bg-black text-white hover:bg-black/90 active:bg-black/95 shadow-lg hover:shadow-xl')} disabled={isLoading || isGoogleLoading || !canSubmitEmail}>
                {isLoading ? (<>
                    <Loader2 className="w-4 h-4 me-2 animate-spin"/>
                    {t("UVkKe5")}
                  </>) : !captchaToken && isEmailValid && email.trim() !== '' ? (<>
                    <Loader2 className="w-4 h-4 me-2 animate-spin"/>
                    {t("8nqlNv")}
                  </>) : (t("acrOoz"))}
              </Button>

              <div className={cn('text-center pt-2', variant === 'marketing' && 'pt-3')}>
                <button type="button" onClick={()=>{
        setMode('signin');
        setError(null);
        setPassword('');
    }} className="text-sm transition-colors text-muted-foreground hover:text-foreground">
                  {t("uCk8r-")} <span className="font-medium text-foreground">{t("odXlk8")}</span>
                </button>
              </div>
            </form>)}

          { /* Sign Up Step 2: OTP Verification */ }
          {mode === 'signup' && signUpStep === 'otp-verification' && (<form onSubmit={handleVerifyOTP} className={cn('space-y-4', variant === 'marketing' && 'space-y-5')}>
              <div className="space-y-3">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">
                    {t.rich("zEnVyu", {
        email,
        emailValue: (chunks)=><span className="font-medium text-foreground">{chunks}</span>
    })}
                  </p>
                </div>

                <div className="flex justify-center py-2">
                  <OTPInput value={otp} onChange={(value)=>{
        setOtp(value);
        setError(null);
    }} onComplete={submitOTP} disabled={isLoading} error={!!error} autoFocus/>
                </div>
              </div>

              <Button type="submit" className={cn('w-full h-11 font-medium transition-all', variant === 'marketing' && 'bg-black text-white hover:bg-black/90 active:bg-black/95 shadow-lg hover:shadow-xl')} disabled={isLoading || !canSubmitOTP}>
                {isLoading ? (<>
                    <Loader2 className="w-4 h-4 me-2 animate-spin"/>
                    {t("8nqlNv")}
                  </>) : (t("12IjJw"))}
              </Button>

              <div className="flex items-center justify-between pt-2">
                <button type="button" onClick={handleBackToEmail} className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1">
                  <ArrowLeft className="w-3 h-3"/>
                  {t("ChATeO")}
                </button>

                <button type="button" onClick={handleResendOTP} disabled={otpCountdown.isActive || isLoading} className={cn('text-sm transition-colors', otpCountdown.isActive || isLoading ? 'text-muted-foreground/50 cursor-not-allowed' : 'text-muted-foreground hover:text-foreground')}>
                  {otpCountdown.isActive ? t("nVe6Mx", {
        seconds: String(otpCountdown.secondsRemaining)
    }) : t("ZD48TW")}
                </button>
              </div>
            </form>)}

          { /* Sign Up Step 3: Password Entry */ }
          {mode === 'signup' && signUpStep === 'password-entry' && (<form onSubmit={handleCompleteSignup} className={cn('space-y-4', variant === 'marketing' && 'space-y-5')}>
              <div className="space-y-2">
                <div className="text-center mb-4">
                  <p className="text-sm text-muted-foreground">
                    {t.rich("v2ATys", {
        email,
        emailValue: (chunks)=><span className="font-medium text-foreground">{chunks}</span>
    })}
                  </p>
                </div>

                <Label htmlFor="signup-password" className="text-sm font-medium">
                  {t("5sg7KC")}
                </Label>
                <Input id="signup-password" name="signup-password" type="password" placeholder={t("Kv7PO-", {
        count: String(passwordPolicy.minLength)
    })} value={password} onChange={(e)=>{
        setPassword(e.target.value);
        setError(null);
    }} minLength={passwordPolicy.minLength} required disabled={isLoading} autoFocus className={cn('h-11 transition-colors', password.trim() !== '' && !isPasswordValid && 'border-white/30 focus-visible:ring-white/20')} autoComplete="new-password"/>
                {password !== '' && (<PasswordRequirements password={password} policy={passwordPolicy} className="pt-1"/>)}
              </div>

              <Button type="submit" className={cn('w-full h-11 font-medium transition-all', variant === 'marketing' && 'bg-black text-white hover:bg-black/90 active:bg-black/95 shadow-lg hover:shadow-xl')} disabled={isLoading || !canSubmitPassword}>
                {isLoading ? (<>
                    <Loader2 className="w-4 h-4 me-2 animate-spin"/>
                    {t("v7Bl2J")}
                  </>) : (t("huqKGl"))}
              </Button>
            </form>)}
        </div>
      </DialogContent>
    </Dialog>);
}
